// Serviço de autenticação - gerencia lógica de login online/offline
import { storageService } from "./storageService";
import logger from "../../../utils/logger";
import { generateMockPlotsResponse } from "../../../utils/mockData";
import { login } from "../../../services/domain/authApiService";
import { getPlots } from "../../../services/domain/plotsApiService";

export const authService = {
  /**
   * Realiza login online através da API
   * @param {string} username - Nome de usuário
   * @param {string} password - Senha
   * @returns {Promise<Object|null>} Dados de autenticação ou null se falhar
   */
  loginOnline: async (username, password) => {
    try {
      logger.info("Iniciando login online", {
        username,
        endpoint: "AUTH"
      });

      const data = await login(username, password);

      logger.debug("Resposta recebida do servidor", {
        dataType: Array.isArray(data) ? 'array' : typeof data,
        dataLength: Array.isArray(data) ? data.length : (data ? Object.keys(data).length : 0),
        hasData: Array.isArray(data) ? (data && data.length > 0) : !!data,
        dataKeys: typeof data === 'object' && data !== null ? Object.keys(data) : null
      });

      let authData = null;

      // Se a resposta é um array (formato antigo)
      if (Array.isArray(data) && data.length > 0) {
        const first = data[0];
        authData = {
          token: first?.token,
          name: first?.name || username,
          email: first?.email || "",
          enterprise: first?.enterprise || "ACM",
          type: first?.type || "",
        };
      }
      // Se a resposta é um objeto (formato novo)
      else if (data && typeof data === 'object' && !Array.isArray(data)) {
        const token =
          data.token ||
          data?.user?.token ||
          (Array.isArray(data.data) && data.data.length > 0 ? data.data[0].token : null);
        authData = {
          token,
          name: data.name || data?.user?.name || username,
          email: data.email || data?.user?.email || "",
          enterprise: data.enterprise || data?.user?.enterprise || "ACM",
          type: data.type || data?.user?.type || "",
        };
      }

      if (authData?.token) {
        logger.success("Login online bem-sucedido", {
          tokenLength: authData.token?.length,
          tokenPreview: authData.token ? `${authData.token.substring(0, 20)}...` : "N/A"
        });
        return authData;
      }

      logger.warn("Usuário não encontrado - token não encontrado na resposta", {
        dataType: Array.isArray(data) ? 'array' : typeof data,
        dataKeys: typeof data === 'object' && data !== null ? Object.keys(data) : null,
        dataPreview: data
      });
      return null;
    } catch (error) {
      logger.error("Erro no login online", error);
      throw error;
    }
  },

  /**
   * Valida credenciais para login offline
   * @param {string} username - Nome de usuário
   * @param {string} password - Senha
   * @returns {boolean} True se as credenciais são válidas
   */
  loginOffline: (username, password) => {
    logger.info("Validando login offline", {
      username
    });

    const savedUser = storageService.getUser();

    if (!savedUser) {
      logger.warn("Nenhum usuário salvo encontrado");
      return false;
    }

    const isValid = (
      savedUser.user === username &&
      savedUser.password === password
    );

    logger.debug("Validação offline", {
      isValid,
      userMatch: savedUser.user === username,
      passwordMatch: savedUser.password === password
    });

    return isValid;
  },

  /**
   * Salva dados de autenticação após login bem-sucedido
   * @param {string|null} token - Token JWT (null para modo offline)
   * @param {string} user - Nome de usuário
   * @param {string} password - Senha
   * @param {string} region - Região selecionada
   * @param {string} enterprise - Empresa
   */
  saveAuthData: (token, user, password, region, enterprise = "ACM") => {
    logger.info("Salvando dados de autenticação", {
      hasToken: !!token,
      user,
      region
    });

    // Limpa dados antigos
    storageService.clearOldData();

    // Salva novos dados
    if (token) {
      storageService.setToken(token);
    }
    localStorage.setItem("enterprise", enterprise);
    storageService.setRegion(region);
    storageService.setUser(user, password);
    storageService.setNameUser(user);

    logger.success("Dados salvos com sucesso", {
      hasToken: !!storageService.getToken(),
      hasUser: !!storageService.getUser(),
      region: storageService.getRegion()
    });
  },

  /**
   * Realiza login mock para cliente_sipremo
   * @param {string} username - Nome de usuário
   * @param {string} password - Senha (qualquer senha é aceita)
   * @returns {Promise<string>} Token mock
   */
  loginMock: async (username, password) => {
    logger.info("Login mock ativado", { username });
    
    // Simular delay de rede
    await new Promise(resolve => setTimeout(resolve, 500));
    
    // Retornar token mock
    const mockToken = `mock_token_${Date.now()}_${username}`;
    logger.success("Login mock bem-sucedido");
    return mockToken;
  },

  /**
   * Busca os plots (talhões) da região selecionada
   * @param {string} region - Região selecionada (RNO, RCO, RRD, Norte, Sul)
   * @param {string} enterprise - Empresa (default: ACM)
   * @returns {Promise<Array|null>} Lista de plots ou null se falhar
   */
  getPlots: async (region, enterprise = "ACM") => {
    // Verificar se está em modo mock
    if (storageService.isMockMode()) {
      logger.info("Buscando plots mock", { region });
      
      // Gerar plots mock
      const mockResponse = generateMockPlotsResponse();
      
      // Se a região for "Norte" ou "Sul", retornar apenas plots dessa região
      if (region === "Norte" || region === "Sul") {
        const plots = mockResponse.dados[region] || [];
        localStorage.setItem("allPlots", JSON.stringify(plots));
        logger.success("Plots mock salvos", { region, totalPlots: plots.length });
        return plots;
      }
      
      // Se for outra região, retornar todos os plots
      const allPlots = [
        ...(mockResponse.dados["Norte"] || []),
        ...(mockResponse.dados["Sul"] || [])
      ];
      localStorage.setItem("allPlots", JSON.stringify(allPlots));
      logger.success("Plots mock salvos", { totalPlots: allPlots.length });
      return allPlots;
    }

    try {
      logger.info("Buscando plots", {
        region,
        enterprise,
        endpoint: "GET_PLOT"
      });

      const plots = await getPlots(enterprise);

      logger.debug("Plots recebidos", {
        dataType: "array",
        dataLength: plots.length
      });

      if (plots.length > 0) {
        // Salva os plots normalizados no localStorage
        localStorage.setItem("allPlots", JSON.stringify(plots));
        logger.success("Plots salvos com sucesso", {
          totalPlots: plots.length
        });
        return plots;
      }

      logger.warn("Nenhum plot encontrado");
      return [];
    } catch (error) {
      logger.error("Erro ao buscar plots", error);
      throw error;
    }
  }
};

